import type { SpeechifyPersistedStoreState } from 'store';
// eslint-disable-next-line no-restricted-imports
import { StateCreator, create } from 'zustand';
import { StateStorage, createJSONStorage, persist } from 'zustand/middleware';

import { SPEECHIFY_VOICE_STORE_NAME } from 'modules/voices/utils/constants';
import { REDUX_IDB_DB_NAME, REDUX_IDB_OBJECT_STORE_NAME } from 'store/constants';
import { withTimeout } from 'utils/promise';

import { IDB_DATABASE_NAME } from '../utils/constants';
import { forceMigrationOnInitialPersist } from '../utils/forceMigrationOnInitialPersist';
import { nestedJsonParse } from '../utils/parse';
import { IndexedDBStorage } from '../utils/storage';
import { registerStore } from './registry';
import { IndexedDBStoreInstance, IndexedDbStoreOptions, StoreType } from './types';

// Track hydration status for all stores
const storeHydrationStatus: Record<string, boolean> = {};

export function createIndexedDbStore<T, PersistedState = T>(initialState: StateCreator<T>, options: IndexedDbStoreOptions<T, PersistedState>) {
  if (options.version && options.version <= 0) {
    throw new Error('IndexedDbStore: version must be greater than 0');
  }

  if (!options.storageName) {
    throw new Error('IndexedDbStore: storageName is required');
  }

  storeHydrationStatus[options.storageName] = false;

  const { backfillStateFromReduxPersist } = options;

  // This special case is for maintaining backwards compatibility for voice store which uses IDB_DATABASE_NAME as the database name in production.
  // In the past, we would use the same database name for all stores, but due to how IndexedDB migration works, we can't have multiple object stores with the same name in the same database,
  // since it would not trigger the `createObjectStore` callback and the database version would not be bumped. (see the IndexedDBStorage co
  // This bug was discovered in WEB-5852 when we introduced the 2nd store that uses IndexedDB which is the new overhaul library store
  const actualDbName = options.storageName === SPEECHIFY_VOICE_STORE_NAME ? IDB_DATABASE_NAME : options.storageName;

  const idb = typeof window !== 'undefined' ? new IndexedDBStorage(options.storageName, actualDbName) : undefined;

  const store = create(
    persist(initialState, {
      skipHydration: typeof window === 'undefined', // skip hydration on SSR
      name: options.storageName,
      storage: forceMigrationOnInitialPersist(createJSONStorage(() => idb as unknown as StateStorage)),
      version: options.version ?? 1,
      migrate: async (state, version) => {
        if (version === 0 && typeof backfillStateFromReduxPersist === 'function') {
          try {
            const speechifyWebDb = new IndexedDBStorage(REDUX_IDB_OBJECT_STORE_NAME, REDUX_IDB_DB_NAME);
            const persistedReduxStateJsonString = (await speechifyWebDb.getItem('persist:root')) || '{}';
            const persistedReduxRootState = nestedJsonParse(persistedReduxStateJsonString) as SpeechifyPersistedStoreState;
            const backfilledState = backfillStateFromReduxPersist(persistedReduxRootState, state as T);
            return backfilledState;
          } catch (e) {
            console.error('Failed to backfill state from redux persist', e);
            return (options.migrate && options.migrate(state, version)) || state;
          }
        }
        return (options.migrate && options.migrate(state, version)) || state;
      },
      partialize: options.partialize ?? (state => state),
      onRehydrateStorage: () => () => {
        // Mark this store as hydrated when rehydration completes
        storeHydrationStatus[options.storageName] = true;
      }
    })
  );

  let isWaitForInitialHydrationCalled = false;
  const waitForInitialHydration = async () => {
    isWaitForInitialHydrationCalled = true;
    return withTimeout(
      new Promise<void>(resolve => {
        if (store.persist.hasHydrated()) {
          resolve();
          return;
        }
        const cleanUp = store.persist.onFinishHydration(() => {
          cleanUp();
          resolve();
        });
      }),
      5000
    );
  };

  const isHydrated = () => {
    return storeHydrationStatus[options.storageName];
  };

  const useStore = <U>(selector: (state: T) => U) => {
    if (!isWaitForInitialHydrationCalled) {
      console.warn('useStore: waitForInitialHydration must be called before calling useStore');
    } else if (!store.persist.hasHydrated()) {
      console.warn('useStore: store is not hydrated yet');
    }
    return store(selector ?? (state => state));
  };

  const subscribe = (listener: (state: T, prevState: T) => void) => {
    if (!isWaitForInitialHydrationCalled) {
      console.warn('subscribe: waitForInitialHydration must be called before calling subscribe');
    } else if (!store.persist.hasHydrated()) {
      console.warn('subscribe: store is not hydrated yet');
    }
    return store.subscribe(listener);
  };

  const getState = () => {
    if (!isWaitForInitialHydrationCalled) {
      console.warn('getState: waitForInitialHydration must be called before calling getState');
    } else if (!store.persist.hasHydrated()) {
      console.warn('getState: store is not hydrated yet');
    }
    return store.getState();
  };

  let cleanupFunctions: (() => void)[] = [];

  const registerOnResetCleanup = (fn: () => void) => {
    cleanupFunctions.push(fn);
  };

  const reset = () => {
    cleanupFunctions.forEach(fn => fn());
    store.persist.rehydrate();
    cleanupFunctions = [];
  };

  const purgePersistentLayer = async () => {
    if (idb) {
      await idb.purge();
    }
    reset();
    cleanupFunctions = [];
  };

  Object.assign(useStore, {
    ...store,
    getState,
    subscribe,
    waitForInitialHydration,
    reset,
    purgePersistentLayer,
    registerOnResetCleanup,
    isHydrated,
    type: StoreType.indexedDB,
    storageName: options.storageName,
    options // Store options for hydration filtering
  });

  const finalStore = useStore as IndexedDBStoreInstance<T> & {
    persist: (typeof store)['persist'];
  };

  registerStore(finalStore);

  return finalStore;
}
